"use client"

import { Placeholder } from "@tiptap/extension-placeholder"
import { EditorContent, useEditor, useEditorState } from "@tiptap/react"
import { BubbleMenu } from "@tiptap/react/menus"
import { StarterKit } from "@tiptap/starter-kit"
import { Bold01, Dotpoints01, Italic01, Link01 } from "@untitledui/icons"
import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * The exact class string the public playlist page renders the description with
 * (`components/playlists/text-html.tsx`). Sharing it is what makes the editor a
 * true preview: what the recipient edits is what publishes — output parity, not
 * an approximation. Keep these in lockstep if either changes.
 */
const PROSE_CLASS =
  "prose prose-sm text-md text-tertiary max-w-none [&_a]:text-primary [&_a]:underline [&_b]:font-semibold [&_b]:text-inherit [&_blockquote]:border-secondary [&_blockquote]:my-3.5 [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_em]:text-inherit [&_em]:italic [&_i]:text-inherit [&_i]:italic [&_ol]:ml-7 [&_ol]:list-decimal [&_strong]:font-semibold [&_strong]:text-inherit [&_u]:text-inherit [&_u]:underline [&_ul]:ml-7 [&_ul]:list-disc"

interface ProseFieldProps {
  /** Initial HTML (uncontrolled editor; mirrors the normal-edit description). */
  initialContent: string
  /** Fires with the editor's HTML on every change. */
  onUpdate: (html: string) => void
  /** Fired when the editor loses focus — the commit point for persistence. */
  onBlur?: () => void
  placeholder?: string
  /** Required — an edit-surface field carries no visible label. */
  ariaLabel: string
  isInvalid?: boolean
  /**
   * Renders the prose at rest — non-editable, no baseline/hover/focus
   * signifier, no selection toolbar.
   */
  readOnly?: boolean
}

/**
 * A borderless rich-text edit-surface for the playlist description: prose that
 * reads as finished content at rest (in the published voice), with the same
 * persistent baseline / hover-ground / focus-ring signifier as EditableText,
 * and a trimmed formatting toolbar that appears only over a selection — chrome
 * on demand, never resting clutter, and reachable on touch (selection, not
 * hover). See docs/design/edit-surfaces.md.
 */
export function ProseField({
  initialContent,
  onUpdate,
  onBlur,
  placeholder = "Write something…",
  ariaLabel,
  isInvalid = false,
  readOnly = false,
}: ProseFieldProps) {
  const editor = useEditor({
    immediatelyRender: false,
    editable: !readOnly,
    content: initialContent,
    extensions: [
      // Headings off — a description is prose, not a document; everything else
      // is configured to emit the same markup the public page styles.
      StarterKit.configure({
        heading: false,
        bulletList: { HTMLAttributes: { class: "list-disc ml-7" } },
        orderedList: { HTMLAttributes: { class: "list-decimal ml-7" } },
        blockquote: {
          HTMLAttributes: { class: "my-3.5 border-l-4 border-secondary pl-4" },
        },
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
          HTMLAttributes: { class: "text-primary underline" },
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass:
          "first:before:text-placeholder first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none first:before:absolute",
      }),
    ],
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        role: "textbox",
        "aria-multiline": "true",
        class: cn(PROSE_CLASS, "min-h-14 w-full outline-none"),
      },
    },
    onUpdate: ({ editor }) => onUpdate(editor.getHTML()),
    onBlur: () => onBlur?.(),
  })

  const active = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive("bold") ?? false,
      isItalic: editor?.isActive("italic") ?? false,
      isBulletList: editor?.isActive("bulletList") ?? false,
      isLink: editor?.isActive("link") ?? false,
    }),
  }) ?? { isBold: false, isItalic: false, isBulletList: false, isLink: false }

  // Reserve the height so the manuscript doesn't jump while the editor mounts.
  if (!editor) return <div className="min-h-14" aria-hidden />

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined
    const url = window.prompt("Link URL", previous ?? "")
    if (url === null) return
    const chain = editor.chain().focus().extendMarkRange("link")
    if (url === "") chain.unsetLink().run()
    else chain.setLink({ href: url }).run()
  }

  return (
    <div
      className={cn(
        "-mx-2 rounded-md px-2 py-1.5",
        !readOnly && [
          "border-secondary border-b transition-colors duration-150 motion-reduce:transition-none",
          "hover:bg-surface-grouped/60",
          "focus-within:bg-surface-grouped focus-within:border-transparent focus-within:ring-2 focus-within:ring-[color:var(--color-focus-ring)] focus-within:ring-inset",
        ],
        isInvalid && "ring-error_subtle ring-1",
      )}
    >
      {!readOnly && (
        <BubbleMenu
          editor={editor}
          className="border-secondary bg-primary flex items-center gap-0.5 rounded-lg border p-1 shadow-lg"
        >
          <ToolbarButton
            label="Bold"
            isActive={active.isBold}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold01 className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            isActive={active.isItalic}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic01 className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Bulleted list"
            isActive={active.isBulletList}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <Dotpoints01 className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            label="Link"
            isActive={active.isLink}
            onClick={setLink}
          >
            <Link01 className="size-4" />
          </ToolbarButton>
        </BubbleMenu>
      )}

      <EditorContent editor={editor} />
    </div>
  )
}

function ToolbarButton({
  label,
  isActive,
  onClick,
  children,
}: {
  label: string
  isActive: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isActive}
      // Keep the editor's text selection while the toolbar takes the click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "text-quaternary flex size-8 items-center justify-center rounded-md transition-colors",
        "hover:bg-secondary hover:text-secondary",
        "focus-visible:outline-2 focus-visible:outline-offset-2",
        isActive && "bg-secondary text-primary",
      )}
    >
      {children}
    </button>
  )
}
